import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import templateService, {
  ReportTemplate,
  TemplateCreatePayload,
  TemplateUpdatePayload,
  PromptUpdatePayload,
  extractErrorMessage,
  TemplateApiError,
} from '../services/templateService';

/**
 * Safely extract a string error message from a thunk rejection.
 * Handles TemplateApiError, Axios errors, plain Error, and raw objects.
 * NEVER returns a non-string value.
 */
function safeRejectMessage(err: unknown, fallback: string): string {
  if (err instanceof TemplateApiError) return err.message;
  return extractErrorMessage(err, fallback);
}

// ── State ─────────────────────────────────────────────────────────────

interface TemplateState {
  templates: ReportTemplate[];
  selectedTemplate: ReportTemplate | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: TemplateState = {
  templates: [],
  selectedTemplate: null,
  loading: false,
  saving: false,
  error: null,
};

// ── Thunks ────────────────────────────────────────────────────────────

export const fetchTemplates = createAsyncThunk(
  'templates/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      return await templateService.listTemplates();
    } catch (err: unknown) {
      return rejectWithValue(safeRejectMessage(err, 'Failed to load templates'));
    }
  },
);

export const createTemplate = createAsyncThunk(
  'templates/create',
  async (payload: TemplateCreatePayload, { rejectWithValue }) => {
    try {
      return await templateService.createTemplate(payload);
    } catch (err: unknown) {
      return rejectWithValue(safeRejectMessage(err, 'Failed to create template'));
    }
  },
);

export const updateTemplate = createAsyncThunk(
  'templates/update',
  async ({ id, payload }: { id: number; payload: TemplateUpdatePayload }, { rejectWithValue }) => {
    try {
      return await templateService.updateTemplate(id, payload);
    } catch (err: unknown) {
      return rejectWithValue(safeRejectMessage(err, 'Failed to update template'));
    }
  },
);

export const updateTemplatePrompts = createAsyncThunk(
  'templates/updatePrompts',
  async (
    { templateId, payload }: { templateId: number; payload: PromptUpdatePayload },
    { rejectWithValue },
  ) => {
    try {
      return await templateService.updateTemplatePrompts(templateId, payload);
    } catch (err: unknown) {
      return rejectWithValue(safeRejectMessage(err, 'Failed to update template prompts'));
    }
  },
);

// ── Slice ─────────────────────────────────────────────────────────────

const templateSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {
    selectTemplate: (state, action: PayloadAction<ReportTemplate | null>) => {
      state.selectedTemplate = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchTemplates
    builder.addCase(fetchTemplates.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchTemplates.fulfilled, (state, action) => {
      state.loading = false;
      state.templates = action.payload;
    });
    builder.addCase(fetchTemplates.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // createTemplate
    builder.addCase(createTemplate.pending, (state) => {
      state.saving = true;
      state.error = null;
    });
    builder.addCase(createTemplate.fulfilled, (state, action) => {
      state.saving = false;
      state.templates.unshift(action.payload);
      state.selectedTemplate = action.payload;
    });
    builder.addCase(createTemplate.rejected, (state, action) => {
      state.saving = false;
      state.error = action.payload as string;
    });

    // updateTemplate
    builder.addCase(updateTemplate.pending, (state) => {
      state.saving = true;
      state.error = null;
    });
    builder.addCase(updateTemplate.fulfilled, (state, action) => {
      state.saving = false;
      const idx = state.templates.findIndex((t) => t.id === action.payload.id);
      if (idx >= 0) state.templates[idx] = action.payload;
      if (state.selectedTemplate?.id === action.payload.id) {
        state.selectedTemplate = action.payload;
      }
    });
    builder.addCase(updateTemplate.rejected, (state, action) => {
      state.saving = false;
      state.error = action.payload as string;
    });

    // updateTemplatePrompts
    builder.addCase(updateTemplatePrompts.pending, (state) => {
      state.saving = true;
      state.error = null;
    });
    builder.addCase(updateTemplatePrompts.fulfilled, (state, action) => {
      state.saving = false;
      const idx = state.templates.findIndex((t) => t.id === action.payload.id);
      if (idx >= 0) state.templates[idx] = action.payload;
      if (state.selectedTemplate?.id === action.payload.id) {
        state.selectedTemplate = action.payload;
      }
    });
    builder.addCase(updateTemplatePrompts.rejected, (state, action) => {
      state.saving = false;
      state.error = action.payload as string;
    });
  },
});

export const { selectTemplate, clearError } = templateSlice.actions;
export default templateSlice.reducer;
